import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import { BookOpen, Briefcase, Globe, Loader2, Sparkles, ArrowRight } from 'lucide-react';

const PageContainer = styled.div`
  min-height: 100vh;
  background: #141414;
  color: #e5e5e5;
  padding: 40px 20px;

  @media (max-width: 768px) {
    padding: 20px 12px;
  }
`;

const ContentWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const Header = styled.header`
  text-align: center;
  margin-bottom: 40px;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 12px;
  background: linear-gradient(135deg, #fff 0%, #a5a5a5 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled.p`
  color: #a3a3a3;
  font-size: 1.1rem;
`;

const SectionTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 16px;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 16px;
  margin-bottom: 32px;
`;

const OptionCard = styled.div`
  background: ${props => props.$selected ? 'rgba(229, 9, 20, 0.1)' : '#141414'};
  border: 1px solid ${props => props.$selected ? '#e50914' : '#333'};
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;

  &:hover {
    transform: translateY(-2px);
    background: ${props => props.$selected ? 'rgba(229, 9, 20, 0.15)' : '#2a2a2a'};
  }
`;

const Input = styled.input`
  width: 100%;
  background: #141414;
  border: 1px solid #333;
  border-radius: 12px;
  padding: 16px;
  color: #fff;
  font-size: 1rem;
  margin-bottom: 32px;

  &:focus {
    outline: none;
    border-color: #e50914;
  }
`;

const GenerateButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #e50914 0%, #b2070f 100%);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 18px;
  font-size: 1.1rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  transition: transform 0.2s;
  box-shadow: 0 4px 15px rgba(229, 9, 20, 0.3);

  &:hover {
    transform: scale(1.02);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;

function AIStudyPage() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [target, setTarget] = useState('Daily');
    const [level, setLevel] = useState('Intermediate');
    const [topic, setTopic] = useState('');
    const [suggestedTopics, setSuggestedTopics] = useState([]);
    const [topicLoading, setTopicLoading] = useState(false);

    // Fetch suggested topics when target changes
    React.useEffect(() => {
        const fetchTopics = async () => {
            setTopicLoading(true);
            try {
                const res = await apiService.getStudyTopics(target);
                if (res.success) {
                    setSuggestedTopics(res.data);
                }
            } catch (err) {
                console.error("Failed to fetch topics", err);
            } finally {
                setTopicLoading(false);
            }
        };
        fetchTopics();
    }, [target]);

    const handleGenerate = async () => {
        if (!topic) return alert('주제를 입력해주세요!');

        setLoading(true);
        try {
            const res = await apiService.generateStudyContent({ target, level, topic });
            if (res.success) {
                // Navigate to SentenceExtractor with generated content
                navigate('/sentence', {
                    state: {
                        initialText: `${res.data.title}\n\n${res.data.content}`,
                        autoAnalyze: true // Optional flag if we want auto-start
                    }
                });
            } else {
                alert('생성 실패: ' + res.message);
            }
        } catch (err) {
            console.error(err);
            alert('오류가 발생했습니다.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <PageContainer>
            <ContentWrapper>
                <Header>
                    <Title>AI 맞춤<br></br> 학습 생성기</Title>
                    <Subtitle>관심 있는 주제를 입력하면 AI가 학습 자료를 만들어줍니다.</Subtitle>
                </Header>

                <SectionTitle><Sparkles size={18} color="#e50914" /> 학습 목표</SectionTitle>
                <Grid>
                    {[
                        { id: 'CSAT', label: '수능 (CSAT)', icon: BookOpen },
                        { id: 'TOEIC', label: '토익 (TOEIC)', icon: Briefcase },
                        { id: 'Daily', label: '일상 회화', icon: Globe },
                    ].map(item => (
                        <OptionCard
                            key={item.id}
                            $selected={target === item.id}
                            onClick={() => setTarget(item.id)}
                        >
                            <item.icon size={24} color={target === item.id ? '#e50914' : '#666'} />
                            <span style={{ fontWeight: 600 }}>{item.label}</span>
                        </OptionCard>
                    ))}
                </Grid>

                <SectionTitle><ArrowRight size={18} color="#e50914" /> 난이도</SectionTitle>
                <Grid>
                    {['Beginner', 'Intermediate', 'Advanced'].map(l => (
                        <OptionCard
                            key={l}
                            $selected={level === l}
                            onClick={() => setLevel(l)}
                        >
                            <span style={{ fontWeight: 600 }}>{l}</span>
                        </OptionCard>
                    ))}
                </Grid>

                <SectionTitle><Sparkles size={18} color="#e50914" /> 추천 주제 (터치하여 선택)</SectionTitle>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '24px' }}>
                    {topicLoading ? (
                        <div style={{ color: '#666', padding: '10px' }}>AI가 요즘 핫한 주제를 찾고 있습니다...</div>
                    ) : (
                        suggestedTopics.map((t, i) => (
                            <button
                                key={i}
                                onClick={() => setTopic(t)}
                                style={{
                                    background: topic === t ? 'rgba(229, 9, 20, 0.2)' : '#222',
                                    border: topic === t ? '1px solid #e50914' : '1px solid #333',
                                    color: topic === t ? '#e50914' : '#aaa',
                                    borderRadius: '20px',
                                    padding: '8px 16px',
                                    cursor: 'pointer',
                                    fontSize: '0.9rem',
                                    transition: 'all 0.2s'
                                }}
                            >
                                {t}
                            </button>
                        ))
                    )}
                </div>

                <SectionTitle>주제 직접 입력</SectionTitle>
                <Input
                    placeholder="원하는 주제를 직접 입력하셔도 됩니다."
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                />

                <GenerateButton onClick={handleGenerate} disabled={loading}>
                    {loading ? (
                        <>
                            <Loader2 size={24} className="animate-spin" />
                            AI가 열심히 작성 중입니다...
                        </>
                    ) : (
                        <>
                            학습 자료 생성하기 <ArrowRight size={24} />
                        </>
                    )}
                </GenerateButton>

            </ContentWrapper>
        </PageContainer>
    );
}

export default AIStudyPage;
